/**
 * @internal
 * Renders BarChart for continuous (linear scale) data. Not part of the public API.
 * Use `BarChart` instead — it automatically selects between linear and ordinal rendering
 * based on the data type, and handles loading and empty states.
 */
import React, { forwardRef, RefObject, useEffect, useMemo, useRef } from 'react';
import {
  extent as d3extent,
  scaleLinear as d3scaleLinear,
  scaleLog as d3scaleLog,
  scalePoint as d3scalePoint,
  scaleUtc as d3scaleUtc,
  ScaleTime as d3ScaleTime,
  utcParse as d3utcParse,
  ScaleLinear as d3ScaleLinear,
} from 'd3';

import { Flexbox, useTheme, useMessageFormatter, isComponent } from '@redsift/design-system';
import intlMessages from './intl';

import {
  AnyScale,
  BarDatum,
  LegendItemDatum,
  LegendVariant,
  LinearData,
  LinearDatum,
  LinearDim,
  TwoCategoryData,
  TwoCategoryDatum,
} from '../../types';
import { BarChartLegendVariant, ComputedBarProps, RenderedLinearBarChartProps, XScaleType } from './types';
import { StyledBarChart } from './styles';
import { BarDirection } from '../Bar';
import { Axis, AxisVariant, getAxisType } from '../Axis';
import { sizeToDimension, statsBy } from './utils';
import { getSortingMethod, isValidDate, mergeLegends } from '../../utils';
import { useColor } from '../../hooks';
import { BarChartContext } from './context';
import { ChartTypeContext } from '../ChartContainer/context';
import { BarChartBars } from './BarChartBars';
import { Legend } from '../Legend';

export const COMPONENT_TYPES = {
  AXES: 'Axis',
  SECTIONS: 'BarChartSection',
  OVERLAY: 'BarChartOverlay',
  BARS: 'BarChartBars',
  LEGENDS: 'Legend',
} as const;

export const getComponentPosition = (child: React.ReactNode) => {
  if (!React.isValidElement(child)) return null;

  const displayName = (child.type as any)?.displayName;

  if (COMPONENT_TYPES.AXES === displayName) return 'axes';
  if (COMPONENT_TYPES.SECTIONS === displayName) return 'sections';
  if (COMPONENT_TYPES.OVERLAY === displayName) return 'columns';
  if (COMPONENT_TYPES.BARS === displayName) return 'bars';
  if (COMPONENT_TYPES.LEGENDS === displayName) return 'legend';

  return null;
};

const getKey = (datum: LinearDatum | TwoCategoryDatum) => (Array.isArray(datum.key) ? datum.key[0] : datum.key);

const filterData = (hasCategory: boolean, data: LinearData | TwoCategoryData, filterFn: any) => {
  if (hasCategory) {
    return (data as TwoCategoryData).filter(filterFn);
  }
  return (data as LinearData).filter(filterFn);
};

const reduceData = (hasCategory: boolean, data: LinearData | TwoCategoryData, reduceFn: any, initialValue: any) => {
  if (hasCategory) {
    return (data as TwoCategoryData).reduce(reduceFn, initialValue) as any;
  }
  return (data as LinearData).reduce(reduceFn, initialValue) as any;
};

const isSameKey = (key: LinearDim, previousKey: LinearDim, xScaleType: XScaleType) =>
  xScaleType === 'Date' || xScaleType === 'dateString'
    ? (key as Date).getTime() === (previousKey as Date).getTime()
    : key === previousKey;

export const RenderedLinearBarChart = forwardRef<HTMLDivElement, RenderedLinearBarChartProps>((props, ref) => {
  const {
    barProps,
    children,
    className,
    data: propsData,
    direction = BarDirection.up,
    disableAnimations,
    id,
    getBarAnchorProps,
    isBarSelected,
    labelDecorator,
    legendDecorator,
    legendVariant,
    legendProps,
    margins: propsMargins,
    onBarClick,
    size,
    sortingMethod: propsSortingMethod,
    barRole,
    colorTheme,
    tooltipVariant,
    tooltipDecorator,
    xAxisVariant: propsXAxisVariant,
    xAxisPlacement,
    xAxisTickFormat: propsXAxisTickFormat,
    xAxisTickPadding,
    xAxisTickRotation,
    xAxisTickSize,
    xAxisTickValues,
    xAxisMinValue,
    xAxisMaxValue,
    xAxisTickRemodelling,
    xAxisStartDateOffset = -1,
    xAxisEndDateOffset = 1,
    yAxisVariant: propsYAxisVariant,
    yAxisPlacement,
    yAxisTickFormat,
    yAxisTickPadding,
    yAxisTickRotation,
    yAxisTickSize,
    yAxisTickValues,
    yAxisMinValue,
    yAxisMaxValue,
    yAxisTickRemodelling,
    xScaleType: propsXScaleType,
    yScaleLogBase,
    dateParser,
    ...forwardedProps
  } = props;

  const cache = useRef<LinearData | TwoCategoryData>();
  const theme = useTheme();

  const format = useMessageFormatter(intlMessages);

  const hasCategory =
    propsData[0] &&
    Array.isArray(propsData[0].key) &&
    propsData[0].key.length >= 2 &&
    propsData[0].key[1] !== null &&
    propsData[0].key[1] !== undefined;

  const sections = useMemo(() => {
    const sectionList: string[][] = [];

    React.Children.forEach(children, (child) => {
      if (isComponent('BarChartSection')(child)) {
        const categories = React.Children.toArray(child.props.children)
          .filter((c) => isComponent('BarChartBars')(c))
          .map((c) => (c as React.ReactElement).props.category);

        sectionList.push(categories);
      }
    });

    return sectionList;
  }, [children]);

  const hasSections = sections.length > 0;
  const sortingMethod = hasSections ? sections.flat() : propsSortingMethod;

  const xScaleType: XScaleType = propsXScaleType
    ? propsXScaleType
    : getKey(propsData[0]) instanceof Date
    ? 'Date'
    : typeof getKey(propsData[0]) === 'number'
    ? 'number'
    : typeof getKey(propsData[0]) === 'string' && isValidDate(getKey(propsData[0]) as string)
    ? 'dateString'
    : undefined;

  const data =
    xScaleType === 'number'
      ? filterData(
          hasCategory,
          propsData,
          (datum: LinearDatum | TwoCategoryDatum) => typeof getKey(datum) === 'number' && !Number.isNaN(getKey(datum))
        )
      : xScaleType === 'dateString'
      ? filterData(hasCategory, propsData, (datum: LinearDatum | TwoCategoryDatum) =>
          isValidDate(getKey(datum) as string)
        )
      : xScaleType === 'Date'
      ? filterData(hasCategory, propsData, (datum: LinearDatum | TwoCategoryDatum) => getKey(datum) instanceof Date)
      : propsData;

  useEffect(() => {
    cache.current = data;
  });

  const svgRef = useRef<SVGSVGElement>();

  const xAxisVariant = propsXAxisVariant ? propsXAxisVariant : AxisVariant.tickValue;
  const yAxisVariant = propsYAxisVariant ? propsYAxisVariant : AxisVariant.default;

  const chartDimensions = sizeToDimension(size!);
  const width = Math.max(
    Math.min(chartDimensions.width, chartDimensions.maxWidth ?? Infinity),
    chartDimensions.minWidth ?? -Infinity
  );
  const height = chartDimensions.height;
  const margins = {
    top: xAxisVariant === AxisVariant.default || xAxisVariant === AxisVariant.tickValue ? 24 : 8,
    left: yAxisVariant === AxisVariant.default || yAxisVariant === AxisVariant.tickValue ? 48 : 8,
    right: yAxisVariant === AxisVariant.default || yAxisVariant === AxisVariant.tickValue ? 48 : 8,
    bottom: xAxisVariant === AxisVariant.default || xAxisVariant === AxisVariant.tickValue ? 24 : 8,
    ...propsMargins,
  };
  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = width - margins.left - margins.right;
  const numberOfBars = filterData(hasCategory, data, (datum: LinearDatum | TwoCategoryDatum) => datum.value).length;
  const gap = barProps?.gap ?? 5;

  let barWidth = chartWidth / (numberOfBars * 2) - gap;

  const scaleX = (() => {
    if (xScaleType === 'number') {
      const domain = d3extent<LinearDatum | TwoCategoryDatum, number>(data, (d) => getKey(d) as number) as [
        number,
        number
      ];
      domain[0] = (xAxisMinValue as number) ?? domain[0];
      domain[1] = (xAxisMaxValue as number) ?? domain[1];
      const gap = domain[1] - domain[0];
      domain[0] -= gap * 0.1;
      domain[1] += gap * 0.1;
      return d3scaleLinear().domain(domain).range([0, chartWidth]).nice();
    } else if (xScaleType === 'dateString' || xScaleType === 'Date') {
      const domain =
        xScaleType === 'dateString'
          ? (d3extent<LinearDatum | TwoCategoryDatum, number>(data, (d) => Date.parse(getKey(d) as string)) as [
              number,
              number
            ])
          : (d3extent<LinearDatum | TwoCategoryDatum, number>(data, (d) => (getKey(d) as Date).valueOf()) as [
              number,
              number
            ]);
      domain[0] = (xAxisMinValue as number) ?? domain[0];
      domain[1] = (xAxisMaxValue as number) ?? domain[1];
      const startDate =
        xAxisTickValues && typeof xAxisTickValues === 'function' && typeof xAxisTickValues.offset === 'function'
          ? xAxisTickValues.offset(new Date(domain[0]), xAxisStartDateOffset)
          : new Date(domain[0]);
      const endDate =
        xAxisTickValues && typeof xAxisTickValues === 'function' && typeof xAxisTickValues.offset === 'function'
          ? xAxisTickValues.offset(new Date(domain[1]), xAxisEndDateOffset)
          : new Date(domain[1]);

      return barProps?.width && typeof barProps.width === 'number'
        ? d3scaleUtc()
            .domain([startDate, endDate])
            .range([barProps.width / 2 + 10, chartWidth - (barProps.width / 2 + 5)])
        : d3scaleUtc()
            .domain([startDate, endDate])
            .range([barWidth, chartWidth - barWidth])
            .nice();
    } else {
      const domain =
        xAxisTickValues && Array.isArray(xAxisTickValues)
          ? (xAxisTickValues as string[])
          : data.map((d) => getKey(d) as string);
      domain[0] = (xAxisMinValue as string) ?? domain[0];
      domain[1] = (xAxisMaxValue as string) ?? domain[1];
      return d3scalePoint().domain(domain).range([0, chartWidth]);
    }
  })();

  const xAxisTickFormat =
    propsXAxisTickFormat ??
    (xScaleType === 'Date' || xScaleType === 'dateString'
      ? (scaleX as d3ScaleTime<number, number, never>).tickFormat()
      : undefined);

  const statsByCategory = statsBy(data as LinearData | TwoCategoryData, sortingMethod!);
  const colorScale = useColor({
    data: statsByCategory,
    colorTheme: colorTheme!,
    category: (d) => d.key as string,
    theme,
  });

  const bars: ComputedBarProps[] = reduceData(
    hasCategory,
    filterData(
      hasCategory,
      data.sort((a, b) => {
        const currentKeyA = hasCategory ? (a as TwoCategoryDatum).key[0] : (a as LinearDatum).key;
        const currentKeyB = hasCategory ? (b as TwoCategoryDatum).key[0] : (b as LinearDatum).key;
        if (xScaleType === 'number') {
          if (currentKeyA < currentKeyB) return -1;
          if (currentKeyA > currentKeyB) return 1;
        } else if (xScaleType === 'dateString') {
          if (Date.parse(currentKeyA as string) < Date.parse(currentKeyB as string)) return -1;
          if (Date.parse(currentKeyA as string) > Date.parse(currentKeyB as string)) return 1;
        } else if (xScaleType === 'Date') {
          if ((currentKeyA as Date).valueOf() < (currentKeyB as Date).valueOf()) return -1;
          if ((currentKeyA as Date).valueOf() > (currentKeyB as Date).valueOf()) return 1;
        } else if (xScaleType === 'string' && xAxisTickValues && Array.isArray(xAxisTickValues)) {
          if (xAxisTickValues.indexOf(currentKeyA) < xAxisTickValues.indexOf(currentKeyB)) return -1;
          if (xAxisTickValues.indexOf(currentKeyA) > xAxisTickValues.indexOf(currentKeyB)) return 1;
        }
        return hasCategory
          ? getSortingMethod(sortingMethod ?? 'asc-key')(
              { key: (a as TwoCategoryDatum).key[1]!, value: a.value! },
              { key: (b as TwoCategoryDatum).key[1]!, value: b.value! }
            )
          : 0;
      }),
      (d: LinearDatum | TwoCategoryDatum) => d.value !== null
    ),
    (previousValue: ComputedBarProps[], currentValue: LinearDatum | TwoCategoryDatum, currentIndex: number) => {
      const currentKeyD = hasCategory ? (currentValue as TwoCategoryDatum).key[0] : (currentValue as LinearDatum).key;
      const formattedKeyD =
        xScaleType === 'dateString'
          ? dateParser
            ? dateParser(currentKeyD as string)
            : (d3utcParse('%Y-%m-%d')(currentKeyD as string) as Date)
          : currentKeyD;
      const category = hasCategory ? (currentValue as TwoCategoryDatum).key[1] ?? 'default' : 'default';
      const isSameSection = !hasSections
        ? true
        : (() => {
            const findSectionIndex = (cat: string) => sections.findIndex((section) => section.includes(cat));
            const currentSection = findSectionIndex(category);
            const prevSection = findSectionIndex(previousValue[currentIndex - 1]?.category);
            return currentSection !== -1 && currentSection === prevSection;
          })();

      previousValue.push({
        category,
        key: formattedKeyD,
        value: currentValue.value as number,
        cumulativeValue: currentValue.value as number,
      });

      if (
        hasCategory &&
        currentIndex > 0 &&
        isSameKey(formattedKeyD, previousValue[currentIndex - 1].key, xScaleType) &&
        isSameSection
      ) {
        previousValue[currentIndex].cumulativeValue =
          previousValue[currentIndex].cumulativeValue + previousValue[currentIndex - 1].cumulativeValue;
      }

      return previousValue;
    },
    [] as ComputedBarProps[]
  ).filter((bar: ComputedBarProps) => yScaleLogBase === undefined || bar.cumulativeValue !== 0);

  const getScaleY = (
    direction: BarDirection,
    from: number = 0,
    to: number = 1,
    categories: string[] = ['default'],
    hasSectionTitle = false
  ) => {
    const domain = d3extent(
      bars.filter((bar) => categories.includes(bar.category)),
      (d) => d.cumulativeValue as number
    ) as [number, number];
    domain[0] = (yAxisMinValue as number) ?? domain[0];
    domain[1] = (yAxisMaxValue as number) ?? domain[1] * (hasSectionTitle ? 1.2 : 1);
    if (domain[1] === 0 && domain[0] === 0) {
      domain[1] = 1;
    }
    return (yScaleLogBase ? d3scaleLog().clamp(true).base(yScaleLogBase) : d3scaleLinear())
      .domain(domain)
      .range(
        direction === 'down' ? [0, chartHeight * to - chartHeight * from] : [chartHeight * to - chartHeight * from, 0]
      )
      .nice();
  };

  if (xScaleType) {
    const minimalGap =
      Math.min(
        ...(bars
          .map((bar, i) => (i > 0 ? Math.abs((bar.key as number) - (bars[i - 1].key as number)) : undefined))
          .filter((k) => k) as number[])
      ) * 0.9;
    barWidth = Math.min(
      barWidth,
      chartWidth / (((scaleX.domain()[1] as number) - (scaleX.domain()[0] as number)) / minimalGap),
      chartWidth /
        (xAxisTickValues && typeof xAxisTickValues !== 'string'
          ? typeof xAxisTickValues === 'number'
            ? xAxisTickValues
            : Array.isArray(xAxisTickValues)
            ? xAxisTickValues.length
            : xAxisTickValues.range(scaleX.domain()[0] as Date, scaleX.domain()[1] as Date).length
          : 1)
    );
  }

  const categories: string[] = sortingMethod && Array.isArray(sortingMethod) ? sortingMethod : [];
  const pivotedTable = bars.reduce((acc, item) => {
    const { key, category, value } = item;
    if (!categories.includes(category)) {
      categories.push(category);
    }
    let formattedKey = xAxisTickFormat ? xAxisTickFormat(key as any) : key.toString();
    formattedKey = Array.isArray(formattedKey) ? formattedKey.join(' ') : formattedKey;

    let entry = acc.find((e) => e.key === formattedKey);
    if (!entry) {
      entry = { key: formattedKey };
      acc.push(entry);
    }

    entry[category] = value;

    return acc;
  }, [] as Array<Record<string, string | number> & { key: string }>);

  const scaleY = getScaleY(direction, 0, 1, categories);

  const { extraLegendItems, ...forwardedLegendProps } = legendProps || {};

  const {
    axisComponents,
    sectionComponents,
    overlayComponents,
    barsComponents,
    legendComponents,
    titleComponent,
    descriptionComponent,
    multipleScaleY,
  } = useMemo(() => {
    const axisComponents: React.ReactElement[] = [];
    const sectionComponents: React.ReactElement[] = [];
    const overlayComponents: React.ReactElement[] = [];
    const barsComponents: React.ReactElement[] = [];
    const legendComponents: React.ReactElement[] = [];
    let titleComponent: React.ReactElement | undefined;
    let descriptionComponent: React.ReactElement | undefined;
    const multipleScaleY: d3ScaleLinear<number, number, never>[] = [];

    React.Children.forEach(children, (child, index) => {
      const position = getComponentPosition(child);

      if (isComponent('ChartContainerTitle')(child)) {
        titleComponent = child;
      }
      if (isComponent('ChartContainerDescription')(child)) {
        descriptionComponent = child;
      }

      if (!position || !React.isValidElement(child)) return;

      let enhancedChild = child;

      if (isComponent('BarChartSection')(child)) {
        const categories = React.Children.toArray(child.props.children)
          .filter((c) => isComponent('BarChartBars')(c))
          .map((c) => (c as React.ReactElement).props.category);

        const scaleY = getScaleY(
          child.props.direction || 'up',
          child.props.from,
          child.props.to,
          categories,
          child.props.title !== ''
        );
        multipleScaleY.push(scaleY);

        enhancedChild = React.cloneElement(child, {
          scaleX: scaleX,
          scaleY: scaleY,
          key: child.key || `section-${child.props.from}-${child.props.to}-${categories.join('-')}`,
          ...child.props,
        });
      }

      if (isComponent('Axis')(child)) {
        const isX = getAxisType(child.props.position) === 'x';

        enhancedChild = React.cloneElement(child, {
          key: child.key || `axis-${child.props.position}-${index}`,
          chartWidth,
          chartHeight,
          scale: isX ? scaleX : scaleY,
          x: child.props.position === 'right' ? chartWidth : 0,
          y: child.props.position === 'bottom' ? chartHeight : child.props.position === 'top' ? 0 : chartHeight,
          disableAnimations,
          length: isX ? chartWidth : chartHeight,
          tickValues: (isX ? xAxisTickValues : yAxisTickValues) ?? 8,
          tickPadding: isX ? xAxisTickPadding : yAxisTickPadding,
          tickRotation: isX ? xAxisTickRotation : yAxisTickRotation,
          tickSize: isX ? xAxisTickSize : yAxisTickSize,
          tickFormat: isX ? xAxisTickFormat : yAxisTickFormat,
          tickRemodelling: isX ? xAxisTickRemodelling : yAxisTickRemodelling,
          variant: isX ? xAxisVariant : yAxisVariant,
          ...child.props,
        });
      }

      if (isComponent('BarChartOverlay')(child)) {
        enhancedChild = React.cloneElement(child, {
          key: child.key || `overlay-${0}-${1}`,
          from: 0,
          to: 1,
          ...child.props,
        });
      }

      if (isComponent('Legend')(child)) {
        enhancedChild = React.cloneElement(child, {
          key: child.key || `legend-${index}`,
          data: (extraLegendItems
            ? mergeLegends(extraLegendItems, statsByCategory).sort(getSortingMethod(sortingMethod!))
            : statsByCategory
          ).map((d) => ({
            ...d,
            color: colorScale?.(d.key)!,
          })),
          ...child.props,
        });
      }

      switch (position) {
        case 'axes':
          axisComponents.push(enhancedChild);
          break;
        case 'sections':
          sectionComponents.push(enhancedChild);
          break;
        case 'columns':
          overlayComponents.push(enhancedChild);
          break;
        case 'bars':
          barsComponents.push(enhancedChild);
          break;
        case 'legend':
          legendComponents.push(enhancedChild);
          break;
      }
    });

    return {
      axisComponents,
      sectionComponents,
      overlayComponents,
      barsComponents,
      legendComponents,
      titleComponent,
      descriptionComponent,
      multipleScaleY,
    };
  }, [children]);

  return (
    <ChartTypeContext.Provider value="bar">
      <BarChartContext.Provider
        value={{
          barProps,
          barRole,
          bars: bars as ComputedBarProps[],
          barWidth,
          cache,
          categories,
          chartHeight,
          chartWidth,
          colorScale,
          direction,
          disableAnimations,
          format,
          gap,
          getBarAnchorProps,
          hasCategory,
          id,
          isBarSelected,
          labelDecorator,
          margins,
          onBarClick,
          scaleX,
          scaleY,
          tooltipVariant,
          tooltipDecorator,
          xAxisTickFormat,
          xAxisTickPadding,
          xAxisTickRemodelling,
          xAxisTickRotation,
          xAxisTickSize,
          xAxisTickValues,
          xAxisVariant,
          xScaleType,
          yAxisTickValues,
          yAxisTickPadding,
          yAxisTickRotation,
          yAxisTickSize,
          yAxisTickFormat,
          yAxisTickRemodelling,
          yAxisVariant,
        }}
      >
        <StyledBarChart
          mode={typeof onBarClick === 'function' || typeof getBarAnchorProps === 'function' ? 'interactive' : 'static'}
          definition={
            hasCategory
              ? format('definition-linear-with-categories', {
                  categoryLength: categories.length,
                  barLength: bars.length,
                })
              : format('definition-linear', { barLength: bars.length })
          }
          xAxisDefinition={format(`x-axis-${xScaleType === 'number' ? 'numbers' : 'dates'}-definition`, {
            numAxis: xAxisPlacement === 'both' ? 2 : 1,
            start: xAxisTickFormat ? xAxisTickFormat(scaleX.domain()[0] as any) : scaleX.domain()[0],
            end: xAxisTickFormat ? xAxisTickFormat(scaleX.domain()[1] as any) : scaleX.domain()[1],
          })}
          yAxisDefinition={
            multipleScaleY.length > 0
              ? multipleScaleY
                  .map((scale) =>
                    format('y-axis-numbers-definition', {
                      numAxis: yAxisPlacement === 'both' ? 2 : 1,
                      start: (scale as d3ScaleLinear<number, number, never>).domain()[0],
                      end: (scale as d3ScaleLinear<number, number, never>).domain()[1],
                    })
                  )
                  .join(' ')
              : format('y-axis-numbers-definition', {
                  numAxis: yAxisPlacement === 'both' ? 2 : 1,
                  start: (scaleY as d3ScaleLinear<number, number, never>).domain()[0],
                  end: (scaleY as d3ScaleLinear<number, number, never>).domain()[1],
                })
          }
          dataTableRepresentation={
            hasCategory
              ? {
                  header: (
                    <thead>
                      <tr>
                        <th scope="col">{xScaleType === 'Date' || xScaleType === 'dateString' ? 'Date' : 'Key'}</th>
                        {categories.map((category) => (
                          <th key={category} scope="col">
                            {category}
                          </th>
                        ))}
                      </tr>
                    </thead>
                  ),
                  body: (
                    <tbody>
                      {pivotedTable.map((row) => (
                        <tr key={row.key}>
                          <th scope="row">{row.key}</th>
                          {categories.map((category) => (
                            <th key={`${row.key}-${category}-${row[category]}`} scope="col">
                              {row[category]}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  ),
                }
              : {
                  header: (
                    <thead>
                      <tr>
                        <th scope="col">{xScaleType === 'Date' || xScaleType === 'dateString' ? 'Date' : 'Key'}</th>
                        <th scope="col">Value</th>
                      </tr>
                    </thead>
                  ),
                  body: (
                    <tbody>
                      {bars.map((datum) => {
                        let key = xAxisTickFormat ? xAxisTickFormat(datum.key as any) : datum.key.toString();
                        key = Array.isArray(key) ? key.join(' ') : key;
                        return (
                          <tr key={key}>
                            <th scope="row">{key}</th>
                            <td>{datum.value}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  ),
                }
          }
          title={titleComponent}
          description={descriptionComponent}
          {...forwardedProps}
          id={id}
          className={className}
          ref={ref as RefObject<HTMLDivElement>}
        >
          <Flexbox flexDirection="column" alignItems="center" gap="8">
            <svg
              ref={svgRef as RefObject<SVGSVGElement>}
              width={width}
              height={height}
              aria-label={
                typeof onBarClick === 'function' || typeof getBarAnchorProps === 'function'
                  ? format('interactive-chart')
                  : format('static-chart')
              }
              aria-hidden="false"
            >
              {axisComponents.length === 0 && sectionComponents.length === 0 ? (
                <g transform={`translate(${margins.left},${margins.top})`} aria-hidden="true">
                  {['bottom', 'both'].includes(xAxisPlacement!) ? (
                    <Axis
                      disableAnimations={disableAnimations}
                      position="bottom"
                      length={chartWidth}
                      scale={scaleX as AnyScale}
                      x={0}
                      y={chartHeight}
                      tickValues={xAxisTickValues ?? 8}
                      tickPadding={xAxisTickPadding}
                      tickRotation={xAxisTickRotation}
                      tickSize={xAxisTickSize}
                      tickFormat={xAxisTickFormat}
                      tickRemodelling={xAxisTickRemodelling}
                      variant={xAxisVariant}
                    />
                  ) : null}
                  {['top', 'both'].includes(xAxisPlacement!) ? (
                    <Axis
                      disableAnimations={disableAnimations}
                      position="top"
                      length={chartWidth}
                      scale={scaleX as AnyScale}
                      x={0}
                      y={0}
                      tickValues={xAxisTickValues ?? 8}
                      tickPadding={xAxisTickPadding}
                      tickRotation={xAxisTickRotation}
                      tickSize={xAxisTickSize}
                      tickFormat={xAxisTickFormat}
                      tickRemodelling={xAxisTickRemodelling}
                      variant={xAxisVariant}
                    />
                  ) : null}
                  {['left', 'both'].includes(yAxisPlacement!) ? (
                    <Axis
                      disableAnimations={disableAnimations}
                      position="left"
                      length={chartHeight}
                      scale={scaleY as AnyScale}
                      x={0}
                      y={chartHeight}
                      tickValues={yAxisTickValues ?? 8}
                      tickPadding={yAxisTickPadding}
                      tickRotation={yAxisTickRotation}
                      tickSize={yAxisTickSize}
                      tickFormat={yAxisTickFormat}
                      tickRemodelling={yAxisTickRemodelling}
                      variant={yAxisVariant}
                    />
                  ) : null}
                  {['right', 'both'].includes(yAxisPlacement!) ? (
                    <Axis
                      disableAnimations={disableAnimations}
                      position="right"
                      length={chartHeight}
                      scale={scaleY as AnyScale}
                      x={chartWidth}
                      y={chartHeight}
                      tickValues={yAxisTickValues ?? 8}
                      tickPadding={yAxisTickPadding}
                      tickRotation={yAxisTickRotation}
                      tickSize={yAxisTickSize}
                      tickFormat={yAxisTickFormat}
                      tickRemodelling={yAxisTickRemodelling}
                      variant={yAxisVariant}
                    />
                  ) : null}
                </g>
              ) : (
                <g transform={`translate(${margins.left},${margins.top})`} aria-hidden="true">
                  {axisComponents}
                </g>
              )}

              {sectionComponents}
              {overlayComponents}

              {barsComponents.length === 0 && sectionComponents.length === 0 ? (
                <g transform={`translate(${margins.left},${margins.top})`} aria-hidden="true">
                  {(hasSections ? sections : [categories]).map((section) =>
                    section.map((category) => <BarChartBars key={category} category={category} />)
                  )}{' '}
                </g>
              ) : (
                <g transform={`translate(${margins.left},${margins.top})`} aria-hidden="true">
                  {barsComponents}
                </g>
              )}
            </svg>
          </Flexbox>
          {legendComponents.length === 0 && hasCategory && legendVariant !== BarChartLegendVariant.none ? (
            <Legend
              data={(extraLegendItems
                ? mergeLegends(extraLegendItems, statsByCategory).sort(getSortingMethod(sortingMethod!))
                : statsByCategory
              ).map((d) => ({
                ...d,
                color: colorScale?.(d.key)!,
              }))}
              variant={
                legendVariant === BarChartLegendVariant.externalLabelValue
                  ? LegendVariant.value
                  : legendVariant === BarChartLegendVariant.externalLabelPercent
                  ? LegendVariant.percent
                  : legendVariant === BarChartLegendVariant.externalLabel
                  ? LegendVariant.label
                  : LegendVariant.custom
              }
              legendDecorator={
                legendDecorator
                  ? (datum: LegendItemDatum, props) => legendDecorator(datum as unknown as BarDatum, props)
                  : undefined
              }
              {...forwardedLegendProps}
            />
          ) : (
            legendComponents
          )}
        </StyledBarChart>
      </BarChartContext.Provider>
    </ChartTypeContext.Provider>
  );
});
